<template>
  <Layout>
    <PageHeader
      :title="$t('master.orthophoto.title')"
      :subTitle="$t('master.orthophoto.subtitle')"
    />
    <div class="clearfix mc-btn-group__list mc-btn-group__list--detail">
      <div class="float-end d-flex">
        <McButtonBack href="/master/" />
      </div>
    </div>
    <b-card>
      <McAlert
        v-if="hasCrudOrthophotoFeature"
        variant="info"
        :text="$t('master.orthophoto.maxData')"
        class="w-50"
        withIcon="true"
      />
      <McGraphqlEditableTable
        :key="`orthophoto-table-${hasCrudOrthophotoFeature}`"
        ref="tableOrthophoto"
        :apiClient="query"
        :fields="fields"
        :hideCheckbox="!hasDeleteScope"
        :canCreate="hasCreateScope"
        :canSelect="hasDeleteScope"
        :canDelete="hasDeleteScope"
        :canEdit="hasCrudOrthophotoFeature && hasUpdateScope"
        :searchable="true"
        :isSelectableField="false"
        :withCard="false"
        :stickyHeader="true"
        trackBy="orthophotoId"
        :refreshData="loading"
        :isEmpty="isEmpty"
        :isLoading="loading"
        :fieldException="['autoZoom']"
        @handleChange="handleOrthophotoEditStart"
        @handleEditCancel="handleOrthophotoEditCancel"
        @beforeSubmit="handleOrthophotoBeforeSubmit"
      >
        <template #addButton>
          <McButton
            v-if="hasCreateScope"
            variant="primary"
            :text="$t('global.addField')"
            icon="icon-outline_plus"
            :disabled="isOrthophotoLimitReached"
            :title="$t('master.orthophoto.maxFileTooltip')"
            @click="addOrthophoto()"
          />
        </template>
        <template #cell(checkbox)="data">
          <OrthophotoLayerControls
            v-if="hasCrudOrthophotoFeature && isEditingReadyLayer(data.item)"
            :canMoveUp="canMoveLayer(data.item, 'up')"
            :canMoveDown="canMoveLayer(data.item, 'down')"
            @move="(direction) => moveLayer(data.item, direction)"
          />
          <McCheckbox
            v-else
            :modelValue="isOrthophotoSelected(data.item)"
            :value="String(data.item.orthophotoId)"
            :disabled="isOrthophotoCheckboxDisabled(data.item)"
            @change="(event) => handleOrthophotoSelect(event, data)"
          />
        </template>
        <template #cell(layer)="{ item }">
          {{ item.layer ?? '-' }}
        </template>
        <template #cell(createdAt)="{ item }">
          <span> {{ momentDateShortMonth(item.createdAt) }}</span>
          <br />
          <span class="text-muted"> {{ momentTime(item.createdAt) }}</span>
        </template>
        <template #[`cell(creator.fullname)`]="{ item }">
          {{ item.creator ? item.creator.fullname : '-' }}
        </template>
        <template #cell(autoZoom)="{ item }">
          <McToggle
            :value="
              editingOrthophotoId === item.orthophotoId ? editingAutoZoom : item.autoZoom
            "
            :disabled="isAutoZoomDisabled(item)"
            @input="(value) => handleAutoZoomChange(value, item)"
          />
        </template>
        <template #cell(status)="{ item }">
          <McBadge
            :text="getStatusText(item.status)"
            :variant="getStatusVariant(item.status)"
            type="status"
          />
        </template>
        <template #emptyState>
          <McEmptyState
            :customFigure="'search'"
            :title="$t('master.orthophoto.noData')"
            :desc="$t('master.orthophoto.noDataDesc')"
            :hasButton="false"
          />
        </template>
      </McGraphqlEditableTable>
    </b-card>
    <ModalAddOrthophoto
      ref="modalAddOrthophoto"
      @created="handleUploadCreated"
      @success="handleUploadSuccess"
    />
  </Layout>
</template>

<script>
import appConfig from '@/app.config'
import PageHeader from '@/components/page-header-v2'
import Layout from '@/router/layouts/main'
import McEmptyState from '@/components/McEmptyState'
import McGraphqlEditableTable from '@/components/McGraphqlEditableTable'
import McToggle from '@/components/McToggle'
import McAlert from '@/components/McAlert'
import { mapGetters } from 'vuex'
import {
  GET_ORTHOPHOTOS,
  UPDATE_ORTHOPHOTO,
  DELETE_ORTHOPHOTO,
} from '@/services/api/master/orthophoto'
import { Helper } from '@/mixin/helper'
import McButtonBack from '@/components/McButtonBack'
import McButton from '@/components/McButton'
import McBadge from '@/components/McBadge'
import McCheckbox from '@/components/McCheckbox'
import ModalAddOrthophoto from './components/ModalAddOrthophoto'
import OrthophotoLayerControls from './components/OrthophotoLayerControls'
import { getOrthophotoFields } from './lib/fields'
import {
  canMoveLayer as canMoveLayerItem,
  getReadyLayerItems,
  isPositiveLayer,
  moveLayerItems,
  restoreLayerItems,
  sortOrthophotosByLayer,
} from './lib/layer'

const STATUS_VARIANTS = {
  Uploading: 'warning',
  Waiting: 'warning',
  Moving: 'warning',
  Translating: 'warning',
  Ready: 'success',
  Failed: 'danger',
  Cancelled: 'danger',
}

const NON_DELETABLE_STATUSES = ['Uploading', 'Moving', 'Translating']
const EXCLUDED_STATUSES_FROM_LIMIT = ['Failed', 'Cancelled']
const STATUS_POLLING_STATUSES = ['Waiting', 'Moving', 'Translating']
const MAX_ORTHOPHOTO_FILES = 3
const LAYER_UPDATE_MODE_REPLACE = 'Replace'
const DEFAULT_ORTHOPHOTO_INTERVAL_MINUTES = 1

export default {
  page: {
    title: 'Master Orthophoto',
    meta: [{ name: 'description', content: appConfig.description }],
  },

  components: {
    Layout,
    PageHeader,
    McEmptyState,
    McGraphqlEditableTable,
    McToggle,
    McButtonBack,
    McButton,
    McBadge,
    McCheckbox,
    McAlert,
    ModalAddOrthophoto,
    OrthophotoLayerControls,
  },
  mixins: [Helper],

  data() {
    return {
      loading: false,
      orthophotos: [],
      autoZoomValues: {},
      isEmpty: false,
      editingOrthophotoId: null,
      editingAutoZoom: false,
      originalLayers: {},
      statusRefreshInFlight: false,
      statusPollingTimer: null,
      updatingAutoZoomId: null,
    }
  },

  computed: {
    ...mapGetters({
      hasAccess: 'identity/hasAccess',
      getFeatureFlagValue: 'identity/getFeatureFlagValue',
    }),

    query() {
      return {
        get: async (filter) => {
          const supportedFilter = { ...filter }
          delete supportedFilter.orderBy
          const res = await this.$apollo.query({
            query: GET_ORTHOPHOTOS,
            fetchPolicy: 'no-cache',
            variables: {
              filter: supportedFilter,
            },
          })

          const orthophotosResponse = res.data.orthophotos.orthophotos
          const visibleOrthophotos = this.hasCrudOrthophotoFeature
            ? orthophotosResponse.data
            : orthophotosResponse.data.filter((item) => item.status === 'Ready')
          const orthophotosData = {
            ...orthophotosResponse,
            pagination: {
              ...orthophotosResponse.pagination,
              ...(!this.hasCrudOrthophotoFeature && {
                total: visibleOrthophotos.length,
              }),
            },
            data: visibleOrthophotos
              .map((item) => ({
                ...item,
                disableCheckbox: NON_DELETABLE_STATUSES.includes(item.status),
                isShowEdit: item.status === 'Ready',
              }))
              .sort(sortOrthophotosByLayer),
          }
          this.isEmpty = orthophotosData.data.length === 0
          this.orthophotos = [...orthophotosData.data]

          return orthophotosData
        },
        delete: async (id) => {
          if (!this.hasDeleteScope) return false

          try {
            const res = await this.$apollo.mutate({
              mutation: DELETE_ORTHOPHOTO,
              variables: {
                input: {
                  orthophotoId: Number(id),
                },
              },
            })

            return res.data.deleteOrthophoto.isSuccessful
          } catch (error) {
            return false
          }
        },
        update: async (name, oldData, newData) => {
          if (!this.hasUpdateScope) return null

          try {
            const input = {
              orthophotoId: Number(oldData.orthophotoId),
            }
            if (
              this.hasCrudOrthophotoFeature &&
              Object.prototype.hasOwnProperty.call(newData, 'label')
            ) {
              input.label = newData.label
            }
            if (newData.autoZoomChanged) {
              input.autoZoom = this.editingAutoZoom
            }
            if (this.hasCrudOrthophotoFeature && newData.layerChanged) {
              input.layer = {
                mode: LAYER_UPDATE_MODE_REPLACE,
                sequence: Number(newData.layerSequence),
              }
            }

            const res = await this.$apollo.mutate({
              mutation: UPDATE_ORTHOPHOTO,
              variables: {
                input,
              },
            })

            const result = res.data.updateOrthophoto
            if (!result.isSuccessful) {
              this.$mcToast.error(result.message || this.$t('global.failed'))
              return null
            }

            this.resetOrthophotoEditDraft()
            return {
              ...oldData,
              ...(this.hasCrudOrthophotoFeature &&
                Object.prototype.hasOwnProperty.call(newData, 'label') && {
                  label: newData.label,
                }),
              ...(newData.autoZoomChanged && {
                autoZoom: input.autoZoom,
              }),
            }
          } catch (error) {
            this.$mcToast.error(error.message || this.$t('global.failed'))
            return null
          }
        },
      }
    },
    fields() {
      return getOrthophotoFields(this.$t.bind(this), this.hasCrudOrthophotoFeature)
    },
    hasCreateScope() {
      return (
        this.hasCrudOrthophotoFeature &&
        this.hasAccess('j1b-orthophoto-master-data:create')
      )
    },
    hasUpdateScope() {
      return this.hasAccess('j1b-orthophoto-master-data:update')
    },
    hasDeleteScope() {
      return (
        this.hasCrudOrthophotoFeature &&
        this.hasAccess('j1b-orthophoto-master-data:delete')
      )
    },
    hasCrudOrthophotoFeature() {
      const featureValue = this.getFeatureFlagValue('crudOrthoPhoto')
      return featureValue === true || featureValue === 1 || featureValue === 'true' || featureValue === '1'
    },
    orthophotoInterval() {
      const interval = Number(this.getFeatureFlagValue('orthophotoInterval'))
      return Number.isFinite(interval) && interval > 0
        ? interval
        : DEFAULT_ORTHOPHOTO_INTERVAL_MINUTES
    },
    hasPendingOrthophotoStatus() {
      return this.orthophotos.some((item) => STATUS_POLLING_STATUSES.includes(item.status))
    },
    isOrthophotoLimitReached() {
      const activeFiles = this.orthophotos.filter(
        (item) => !EXCLUDED_STATUSES_FROM_LIMIT.includes(item.status)
      )
      return activeFiles.length >= MAX_ORTHOPHOTO_FILES
    },
  },

  watch: {
    hasPendingOrthophotoStatus: {
      immediate: true,
      handler() {
        this.syncStatusPolling()
      },
    },
    orthophotoInterval() {
      this.syncStatusPolling()
    },
  },

  mounted() {
    this.$root.$on('file-upload-complete', this.handleFileUploadComplete)
    this.$root.$on('file-upload-canceled', this.handleFileUploadCanceled)
    window.addEventListener('focus', this.handleStatusPollingResume)
    window.addEventListener('online', this.handleStatusPollingResume)
    document.addEventListener('visibilitychange', this.handleStatusPollingVisibility)
  },

  beforeDestroy() {
    this.$root.$off('file-upload-complete', this.handleFileUploadComplete)
    this.$root.$off('file-upload-canceled', this.handleFileUploadCanceled)
    window.removeEventListener('focus', this.handleStatusPollingResume)
    window.removeEventListener('online', this.handleStatusPollingResume)
    document.removeEventListener('visibilitychange', this.handleStatusPollingVisibility)
    this.stopStatusPolling()
  },

  methods: {
    handleFileUploadComplete(upload) {
      if (upload.uploadType === 'orthophoto') this.handleUploadSuccess()
    },
    handleFileUploadCanceled(upload) {
      if (upload.uploadType === 'orthophoto') this.refreshOrthophotos()
    },
    handleUploadCreated() {
      this.refreshOrthophotos(true)
    },
    async refreshOrthophotos(resetToFirstPage = false) {
      const table = this.$refs.tableOrthophoto
      if (!table || this.statusRefreshInFlight || this.editingOrthophotoId) return

      this.statusRefreshInFlight = true
      try {
        await table.getData({
          limit: table.limit,
          page: resetToFirstPage ? 1 : table.page,
          sorts: table.stringSorts(),
          search: table.search || undefined,
        })
      } catch {
        // The existing table data remains available when this refresh fails.
      } finally {
        this.statusRefreshInFlight = false
      }
    },
    syncStatusPolling() {
      this.stopStatusPolling()
      if (!this.hasPendingOrthophotoStatus) return

      this.statusPollingTimer = window.setInterval(() => {
        if (document.hidden || !navigator.onLine) return
        this.refreshOrthophotos()
      }, this.orthophotoInterval * 60 * 1000)
    },
    stopStatusPolling() {
      if (!this.statusPollingTimer) return
      window.clearInterval(this.statusPollingTimer)
      this.statusPollingTimer = null
    },
    handleStatusPollingVisibility() {
      if (!document.hidden) this.handleStatusPollingResume()
    },
    handleStatusPollingResume() {
      if (this.hasPendingOrthophotoStatus) this.refreshOrthophotos()
    },
    handleOrthophotoEditStart({ item }) {
      if (item.status !== 'Ready') return

      this.editingOrthophotoId = item.orthophotoId
      this.editingAutoZoom = Boolean(item.autoZoom)
      this.originalLayers = this.getReadyLayerItems().reduce(
        (layers, orthophoto) => ({
          ...layers,
          [orthophoto.orthophotoId]: orthophoto.layer,
        }),
        {}
      )
    },
    handleOrthophotoEditCancel() {
      this.restoreLayerDraft()
      this.resetOrthophotoEditDraft()
    },
    handleOrthophotoBeforeSubmit(data, item, resolve) {
      if (this.editingAutoZoom !== Boolean(item.autoZoom)) {
        data.autoZoomChanged = true
      }
      if (this.hasCrudOrthophotoFeature) {
        const originalLayer = Number(this.originalLayers[item.orthophotoId])
        const draftLayer = Number(item.layer)
        if (
          Number.isInteger(originalLayer) &&
          originalLayer > 0 &&
          Number.isInteger(draftLayer) &&
          draftLayer > 0 &&
          originalLayer !== draftLayer
        ) {
          data.layerChanged = true
          data.layerSequence = draftLayer
        }
      }
      resolve({ success: true })
    },
    isAutoZoomDisabled(item) {
      if (!this.hasUpdateScope || this.updatingAutoZoomId === item.orthophotoId) return true
      if (!this.hasCrudOrthophotoFeature) return false
      return this.editingOrthophotoId !== item.orthophotoId
    },
    async handleAutoZoomChange(value, item) {
      if (this.hasCrudOrthophotoFeature) {
        this.editingAutoZoom = value
        return
      }
      if (!this.hasUpdateScope || this.updatingAutoZoomId !== null) return

      const previousValue = Boolean(item.autoZoom)
      const nextValue = Boolean(value)
      this.updatingAutoZoomId = item.orthophotoId
      this.$set(item, 'autoZoom', nextValue)

      try {
        const response = await this.$apollo.mutate({
          mutation: UPDATE_ORTHOPHOTO,
          variables: {
            input: {
              orthophotoId: Number(item.orthophotoId),
              autoZoom: nextValue,
            },
          },
        })
        const result = response.data.updateOrthophoto
        if (!result.isSuccessful) {
          throw new Error(result.message || this.$t('global.failed'))
        }
        this.$mcToast.success(this.$t('global.alert.text.changed'))
      } catch (error) {
        this.$set(item, 'autoZoom', previousValue)
        this.$mcToast.error(error.message || this.$t('global.failed'))
      } finally {
        this.updatingAutoZoomId = null
      }
    },
    resetOrthophotoEditDraft() {
      this.editingOrthophotoId = null
      this.editingAutoZoom = false
      this.originalLayers = {}
    },
    getReadyLayerItems() {
      const items = this.$refs.tableOrthophoto?.items || this.orthophotos
      return getReadyLayerItems(items)
    },
    isEditingReadyLayer(item) {
      return (
        item.orthophotoId === this.editingOrthophotoId &&
        item.status === 'Ready' &&
        isPositiveLayer(item.layer)
      )
    },
    canMoveLayer(item, direction) {
      return canMoveLayerItem(item, this.originalLayers, direction)
    },
    moveLayer(item, direction) {
      const table = this.$refs.tableOrthophoto
      if (!table) return

      table.items = moveLayerItems(table.items, item, this.originalLayers, direction)
    },
    restoreLayerDraft() {
      const table = this.$refs.tableOrthophoto
      if (!table || !Object.keys(this.originalLayers).length) return

      table.items = restoreLayerItems(table.items, this.originalLayers)
    },
    isOrthophotoSelected(item) {
      return Boolean(this.$refs.tableOrthophoto?.selectedIds[item.orthophotoId])
    },
    isOrthophotoCheckboxDisabled(item) {
      return (
        Boolean(this.editingOrthophotoId) ||
        !this.hasDeleteScope ||
        Boolean(item.disableCheckbox)
      )
    },
    handleOrthophotoSelect(event, data) {
      this.$refs.tableOrthophoto?.handleSelect(event, data)
    },
    getStatusText(status) {
      if (!STATUS_VARIANTS[status]) return status || '-'
      return this.$t(`master.orthophoto.status.${status.toLowerCase()}`)
    },
    getStatusVariant(status) {
      return STATUS_VARIANTS[status] || 'secondary'
    },
    handleUploadSuccess() {
      window.setTimeout(() => this.refreshOrthophotos(true), 500)
    },
    addOrthophoto() {
      if (!this.hasCreateScope) return
      this.$refs.modalAddOrthophoto.show()
    },
  },
}
</script>

<style lang="scss" scoped>
::v-deep .editable-table td:first-child {
  vertical-align: middle;
}
</style>
